<template>
  <div class="select-parent">
    <label v-show="showLabel" :id="`${this.name}-label`">
      {{ label || placeHolder }}</label
    >
    <div
      class="select-container"
      :class="[disabled ? 'disabled' : '', direction === 'rtl' ? 'rtl' : '']"
      @mousedown="onMousedown"
      v-on:keydown="onKeydown"
    >
      <div
        :disabled="disabled"
        :aria-disabled="disabled"
        role="combobox"
        :ref="this.name"
        :id="this.name"
        :aria-labelledby="`${this.name}-label`"
        :aria-controls="listBoxId"
        aria-haspopup="listbox"
        tabindex="0"
        :aria-expanded="isDropdownOpen.toString()"
        @mousedown="toggleDropDown"
        :class="['combo-box', 'fill-button', isDropdownOpen ? 'open' : '']"
        @focus="onFocus"
        @blur="onBlur"
      >
        {{
          selectedItem ? getItemLabel(selectedItem) : placeHolder || defaulttext
        }}
      </div>
      <div
        v-show="showItems"
        role="listbox"
        :id="listBoxId"
        :class="['list-box', listTop ? 'display-top' : '']"
        :aria-labelledby="`${this.name}-label`"
      >
        <div
          tabindex="-1"
          role="option"
          v-on:keydown.enter="selectItem(item, index)"
          v-on:keydown.space="selectItem(item, index)"
          @click="selectItem(item, index)"
          v-for="(item, index) in items"
          :key="getItemKey(item)"
          :name="getItemKey(item)"
          :aria-selected="(item === selectedItem).toString()"
          :class="[
            'list-item',
            getItemKey(item) === getItemKey(selectedItem) ? 'selected' : '',
          ]"
        >
          <slot name="item" v-bind="normalizeOptionForSlot(item)">
            {{ getItemLabel(item) }}
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    selectName: String,
    label: {
      type: String,
      value: "",
    },
    itemLabel: String,
    items: [],
    placeHolder: {
      type: String,
      value: "",
    },
    value: {},
    itemId: String,
    ariaLabel: String,
    disabled: Boolean,
    listTop: Boolean,
    direction: {
      type: String,
      value: "ltr",
    },
  },
  data() {
    return {
      isDropdownOpen: false,
      selectedItem: undefined,
      itemIndex: 0,
      dropDownOpening: false,
      defaulttext: "Select an option",
      defaultName: "select",
      listBoxId: `${this.selectName}-listBox`,
      searchString: "",
    };
  },
  computed: {
    isTrackingValues() {
      return (
        typeof this.value === "undefined" ||
        // eslint-disable-next-line no-prototype-builtins
        this.$options.propsData.hasOwnProperty("reduce")
      );
    },
    showItems() {
      return this.isDropdownOpen;
    },
    showLabel() {
      return (
        this.label !== undefined ||
        (this.placeHolder !== undefined &&
          this.selectedItem &&
          Object.keys(this.selectedItem).length !== 0)
      );
    },
    fillButton() {
      return (this.placeHolder === "" && this.label === "") || !this.showLabel;
    },
    randomID() {
      return Math.random() * 100;
    },
    name() {
      return this.selectName || this.defaultName;
    },
  },
  methods: {
    onFocus() {
      if (!this.isDropdownOpen) {
        this.isDropdownOpen = true;
        this.dropDownOpening = true;
        if (this.selectedItem) {
          this.setInitialFocus();
        }
      }
      if (this.disabled) {
        this.isDropdownOpen = false;
        return;
      }
    },
    onBlur() {
      if (this.dropDownOpening) {
        this.dropDownOpening = false;
      } else {
        this.isDropdownOpen = false;
        return;
      }
      // Fixed bug where no-options message could not be closed
      if (this.items.length === 0) {
        this.isDropdownOpen = false;
        return;
      }
    },
    onMousedown() {
      this.dropDownOpening = true;
    },
    getSearchString(char) {
      // reset typing timeout and start new timeout
      // this allows us to make multiple-letter matches, like a native select
      if (typeof this.searchTimeout === "number") {
        window.clearTimeout(this.searchTimeout);
      }

      this.searchTimeout = window.setTimeout(() => {
        this.searchString = "";
      }, 500);

      // add most recent letter to saved search string
      this.searchString += char;
      return this.searchString;
    },
    onKeydown(e) {
      console.log(e);
      const key = e.key;

      if (
        key === "Backspace" ||
        key === "Clear" ||
        (key.length === 1 && key !== " ")
      ) {
        this.searchList(key);
        return;
      }

      switch (key) {
        case "Tab":
        case "Escape":
          this.isDropdownOpen = false;
          break;
        case "ArrowDown":
          e.preventDefault();
          if (this.isDropdownOpen) {
            this.moveNext();
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          if (this.isDropdownOpen) {
            this.movePrevious();
          }
          break;
        default:
      }
    },
    searchList(key) {
      const search = this.getSearchString(key);
      const index = this.getIndexByLetter(
        this.items,
        search,
        this.itemIndex + 1
      );
      if (index > -1) {
        let items = document
          .getElementById(this.listBoxId)
          .getElementsByClassName("list-item");
        items[index].focus();
        this.itemIndex = index;
      }
    },
    moveNext() {
      if (this.itemIndex === this.items.length - 1) {
        return;
      }

      if (this.dropDownOpening && !this.selectedItem) {
        this.itemIndex = 0;
      } else {
        this.itemIndex++;
      }

      let items = document
        .getElementById(this.listBoxId)
        .getElementsByClassName("list-item");
      items[this.itemIndex].focus();
    },
    movePrevious() {
      if (this.itemIndex === 0) {
        return;
      }
      let items = document
        .getElementById(this.listBoxId)
        .getElementsByClassName("list-item");
      this.itemIndex--;
      items[this.itemIndex].focus();
    },
    getItemKey(item) {
      if (item === null) {
        return;
      }

      if (typeof item !== "object") {
        return item;
      }

      try {
        // eslint-disable-next-line no-prototype-builtins
        return item.hasOwnProperty(this.itemId)
          ? item[this.itemId]
          : this.sortAndStringify(item);
      } catch (e) {
        const warning = "Could not stringify this option";
        return console.warn(warning, item, e);
      }
    },
    getItemLabel(item) {
      if (item === null) {
        return;
      }

      if (typeof item === "object") {
        // eslint-disable-next-line no-prototype-builtins
        if (!item.hasOwnProperty(this.itemLabel)) {
          return console.warn(
            `Label key "item.${this.itemLabel}" does not` +
              ` exist in items object ${JSON.stringify(item)}.\n`
          );
        }
        return item[this.itemLabel];
      }
      return item;
    },
    normalizeOptionForSlot(option) {
      return typeof option === "object" ? option : { [this.label]: option };
    },
    sortAndStringify(sortable) {
      const ordered = {};

      Object.keys(sortable)
        .sort()
        .forEach((key) => {
          ordered[key] = sortable[key];
        });

      return JSON.stringify(ordered);
    },
    toggleDropDown(e) {
      if (!this.isDropdownOpen) {
        this.isDropdownOpen = true;
        if (this.selectedItem) {
          this.setInitialFocus();
        }
      } else {
        e.preventDefault();
        this.isDropdownOpen = false;
        this.$refs[this.name].blur();
      }
      if (this.disabled) {
        this.isDropdownOpen = false;
        return;
      }
    },
    setInitialFocus() {
      this.dropDownOpening = true;
      if (this.itemIndex === 0) {
        this.selectedItem = this.items[this.itemIndex];
      } else {
        this.itemIndex = this.items.indexOf(this.selectedItem);
      }
    },
    reduce: (item) => item,
    setInternalValueFromOptions(value) {
      this.$data._value = value;
      this.selectedItem = value;

      let index = 0;
      if (this.items) {
        index = this.items.indexOf(value);
      }
      this.itemIndex = index >= 0 ? index : 0;
    },
    updateValue(value) {
      if (typeof this.value === "undefined") {
        // Vue select has to manage value
        this.$data._value = value;
      }

      if (value !== null) {
        if (Array.isArray(value)) {
          value = value.map((val) => this.reduce(val));
        } else {
          value = this.reduce(value);
        }
      }

      this.$emit("input", value);
    },
    selectItem(item, index) {
      this.itemIndex = index;
      this.selectedItem = item;
      this.isDropdownOpen = !this.isDropdownOpen;
      this.updateValue(item);
      this.$emit("item:selected", item);
    },
    filterOptions(options = [], filter, exclude = []) {
      console.log(options);
      return options.filter((option) => {
        const matches =
          this.getItemLabel(option)
            .toLowerCase()
            .indexOf(filter.toLowerCase()) === 0;
        return matches && exclude.indexOf(option) < 0;
      });
    },
    getIndexByLetter(options, filter, startIndex = 0) {
      const orderedOptions = [
        ...options.slice(startIndex),
        ...options.slice(0, startIndex),
      ];
      const firstMatch = this.filterOptions(orderedOptions, filter)[0];
      const allSameLetter = (array) =>
        array.every((letter) => letter === array[0]);

      // first check if there is an exact match for the typed string
      if (firstMatch) {
        return options.indexOf(firstMatch);
      }

      // if the same letter is being repeated, cycle through first-letter matches
      else if (allSameLetter(filter.split(""))) {
        const matches = this.filterOptions(orderedOptions, filter[0]);
        return options.indexOf(matches[0]);
      }

      // if no matches, return -1
      else {
        return -1;
      }
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.setInternalValueFromOptions(val);
      },
    },
  },
};
</script>

<style>
@import "@/assets/css/font-awesome.css";

.select-parent {
  width: 100%;
  label {
    display: block;
    padding: 0.05rem 0.25rem;
    width: 100%;
    margin: 0 auto;
    font-size: 0.85rem !important;
    font-weight: 500;
  }
}

.rtl {
  .combo-box::before {
    left: 0.8rem !important;
    right: unset;
  }
}

.select-container {
  position: relative;
  width: 100% !important;
  border: solid 0.1rem #cacaca !important;
  border-bottom: solid 0.15rem #0b5583 !important;
  color: black;

  .combo-box,
  .list-box {
    display: block;
    width: 100%;
    margin: 0 auto;
    background: #fff;
    border: 0;
    font-size: 1rem;
  }

  .combo-box {
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    padding: 0rem 0.6rem 0.4rem;

    &::before {
      font-size: 1em;
      color: #3f403b;
      opacity: 0.7;
      font-family: "FontAwesome";
      content: "\f078";
      vertical-align: middle;
      font-weight: 500;
      position: absolute;
      top: -0.4rem;
      right: 0.8rem;
      -webkit-transition: -webkit-transform 0.2s ease-in-out;
      -ms-transition: -ms-transform 0.2s ease-in-out;
      transition: transform 0.2s ease-in-out;
    }

    &:focus-visible {
      outline: 0;
    }
  }

  .combo-box.fill-button {
    min-height: 45px;
    margin-top: 5px;
    &::before {
      top: unset;
    }
  }

  .combo-box.open {
    &::before {
      -moz-transform: rotate(180deg);
      -webkit-transform: rotate(180deg);
      -o-transform: rotate(180deg);
      -ms-transform: rotate(180deg);
      transform: rotate(180deg);
    }
  }

  .list-box {
    color: #3f403b;
    position: absolute;
    padding: 0.7rem 0.8rem;
    left: 0;
    right: 0;
    top: 3.5rem;
    max-height: 600px;
    overflow-y: auto;
    list-style-type: none;
    padding: 0;
    margin: 0.1rem 0.1rem;
    transform-origin: top left;
    pointer-events: auto;
    z-index: 2;
    box-shadow: 0 0 5px 1px #3f403b;

    .list-item {
      padding: 0.3rem 0.8rem;
      outline: 0 !important;
      cursor: pointer;
      position: relative;

      &::before {
        font-family: "FontAwesome";
        content: "\f00c";
        vertical-align: middle;
        font-weight: 900;
        position: absolute;
        right: 1em;
        opacity: 0;
      }

      &:hover {
        background: #0b5583;
        color: #fff;
        opacity: 0.5;
      }

      &.selected {
        background: #0b5583;
        color: #fff;
        opacity: 1 !important;
      }

      &:focus {
        background: #0b5583;
        color: #fff;
        opacity: 0.8;
      }
    }
  }

  .list-box.display-top {
    top: auto !important;
    bottom: calc(100% + 5px);
    max-height: 300px !important;
  }

  &.disabled,
  &.disabled div,
  &.disabled button,
  &.disabled label {
    opacity: 0.9;
    background: rgb(236, 236, 236);
    cursor: not-allowed;
    font-weight: 600;
  }
}
</style>
